import { useState } from 'react';
import { Typography, Grid, IconButton, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  ArrayField,
  useArrayFieldItemState,
  useArrayFieldItemApi,
  useArrayFieldApi,
} from 'informed';
import { NumberInput, TextField } from '@calefy-inc/informedMaterial';
import { Language } from '../../../../Typescript/classes';
import Bugsnag from '@bugsnag/js';
import { errorify } from '../../../../util';
import { ProgramBuilderComponentProps } from '../types';
import type { $TSFixMe, GenericObject } from '@calefy-inc/utilityTypes';
import { VALUE_SEPARATOR as INPUT_TOGGLE_VALUE_SEPARATOR } from '../../InformedMaterialFills/InformedMaterialObligateInputToggleMultiple';

type MultiOptionItemProps = {
  languages: Array<Language>;
  classes: GenericObject;
  fields: $TSFixMe;
};

function MultiOptionItem({ languages, classes, fields }: MultiOptionItemProps) {
  const [previousLabels, setPreviousLabels] = useState({}); // we need this to tell when a label switches from a number -> not, in which case we should clear the associated label (since it is almost surely not correct)
  const { remove, setValue } = useArrayFieldItemApi();
  const { index, values } = useArrayFieldItemState();
  const arrayFieldApi = useArrayFieldApi();

  return (
    <>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='center'
        spacing={1}
        style={{
          marginBottom: '2em',
        }}
      >
        <Grid item xs={5}>
          {languages.map((language) => {
            return (
              <TextField
                onChange={
                  language.equals(Language.english)
                    ? // @ts-expect-error
                      ({ value }) => {
                        // if the label is a number, set the value to that automatically
                        if (typeof value === 'string') {
                          const convertedLabel = value.replace(/[$,]/gi, '');
                          const isNumber =
                            convertedLabel !== '' &&
                            !Number.isNaN(Number(convertedLabel));

                          const wasPreviousNumber =
                            // @ts-expect-error
                            previousLabels[index] !== '' &&
                            !Number.isNaN(
                              // @ts-expect-error
                              Number(previousLabels[index]),
                            );

                          if (isNumber) {
                            setValue(`value`, convertedLabel);
                          } else if (!isNumber && wasPreviousNumber) {
                            setValue(`value`, '');
                          }

                          if (!isNumber) {
                            const stringLabel = value;
                            const fieldValue = values?.value;
                            //console.log({
                            //   fieldValue,
                            //   stringLabel,
                            //   previousLabels,
                            // });
                            if (
                              fieldValue === '' ||
                              fieldValue === undefined ||
                              fieldValue ===
                                // @ts-expect-error
                                previousLabels[index]
                            ) {
                              setValue(`value`, stringLabel);
                            }
                          }
                        }

                        setPreviousLabels((old) => ({
                          ...old,
                          [index]: values?.value,
                        }));
                      }
                    : undefined
                }
                className={classes.textField}
                classes={classes}
                label={`Label #${index + 1} (${language.fullName})`}
                //helperText="Enter the label for the option. This will be displayed on a button or as a select option"
                name={`${language.generatePrefixedString('labels')}`}
                variant='standard'
              />
            );
          })}
        </Grid>
        <Grid item xs={5}>
          <TextField
            className={classes.textField}
            classes={classes}
            label={`Value #${index + 1}`}
            helperText='Enter the value for the option. This is the actual value of the button or select option'
            name={`value`}
            variant='standard'
            validate={
              // @ts-expect-error
              (value) => {
                if (!value) {
                  return 'This field is required';
                }
                if (value.includes(INPUT_TOGGLE_VALUE_SEPARATOR)) {
                  return `The value cannot contain the special character '${INPUT_TOGGLE_VALUE_SEPARATOR}'`;
                }
                return undefined;
              }
            }
          />
        </Grid>
        <Grid item xs={1}>
          <Tooltip title='Move option up'>
            <span>
              <IconButton
                aria-label='move option up'
                disabled={index === 0}
                onClick={() => {
                  try {
                    arrayFieldApi.swap(index, index - 1);
                  } catch (e) {
                    Bugsnag.notify(errorify(e));
                    console.error(
                      `Error swapping fields ${index - 1}, ${index}:`,
                      e,
                    );
                  }
                }}
              >
                <ArrowUpwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Move option down'>
            <span>
              <IconButton
                aria-label='move option down'
                disabled={index === fields.length - 1}
                onClick={() => {
                  try {
                    arrayFieldApi.swap(index, index + 1);
                  } catch (e) {
                    Bugsnag.notify(errorify(e));
                    console.error(
                      `Error swapping fields ${index}, ${index + 1}:`,
                      e,
                    );
                  }
                }}
              >
                <ArrowDownwardIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <IconButton
            disabled={fields.length <= 2}
            aria-label='delete option'
            color='primary'
            size='small'
            onClick={remove}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    </>
  );
}

export default function MultiOptionsComponent({
  classes = {},
  languages,
}: ProgramBuilderComponentProps) {
  return (
    <>
      <Typography variant='caption' sx={{ mt: 2 }} color='textSecondary'>
        At least 2 options are required.
      </Typography>
      <Typography variant='caption' color='textSecondary'>
        If you are entering a numerical value, do not enter any formatting
        (spaces, $, %).
      </Typography>
      <ArrayField name='props_blob_options'>
        {(arrayFieldProps) => {
          // @ts-expect-error
          const { add, fields, arrayFieldApi } = arrayFieldProps;
          if (fields.length < 2) {
            add(); // this line throws two warnings when you go to create a multioptions question in a form
          }
          return (
            <>
              <ArrayField.Items>
                {() => (
                  <MultiOptionItem
                    languages={languages}
                    classes={classes}
                    fields={fields}
                  />
                )}
              </ArrayField.Items>

              <Tooltip title='Add Option'>
                <IconButton
                  aria-label='Add Option'
                  type='button'
                  color='primary'
                  onClick={() => add()}
                  sx={{
                    my: 2,
                    alignSelf: 'center',
                  }}
                  size='large'
                >
                  <AddCircleIcon color='primary' sx={{ fontSize: 32 }} />
                </IconButton>
              </Tooltip>
            </>
          );
        }}
      </ArrayField>
      <TextField
        name='defaultValue'
        label='Default Value (ensure that it is associated with a label)'
      />
      <NumberInput
        name='props_blob_numColumns'
        label='# Columns (leave blank or 0 to not specify)'
      />
    </>
  );
}
